import React, { useState, useMemo, useRef } from 'react';
import { Select, Spin, Empty } from 'antd';
import type { SelectProps } from 'antd/es/select';
import debounce from 'lodash.debounce';
import { GetUserList } from '../../services/userAPI';
import { getPaginatedProviders } from '../../services/adminProviderApi';


export interface UserValue {
  label: string;
  value: string;
}

interface UserSearchSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<UserValue[]>;
  debounceTimeout?: number;
}

function DebounceSelect<
  ValueType extends { key?: string; label: React.ReactNode; value: string | number } = any,
>({ fetchOptions, debounceTimeout = 300, ...props }: UserSearchSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions as any);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {...props}
      options={options}
    />
  );
}

export const UserSearchSelect: React.FC<Omit<UserSearchSelectProps, 'fetchOptions'>> = (props) => {
    const fetchUserList = async (search: string): Promise<UserValue[]> => {
        try {
            const response = await GetUserList({
                page: 1,
                take: 20,
                search: search
            });
            return response.data.map((user: any) => ({
                label: `${user.firstName} ${user.lastName} (${user.email || user.mobile})`,
                value: user.id.toString(),
            }));
        } catch (error) {
            console.error('Error fetching users:', error);
            return [];
        }
    };

    return (
        <DebounceSelect
            showSearch
            placeholder="Escriba nombre, email o teléfono"
            fetchOptions={fetchUserList}
            {...props}
        />
    );
};

export const DriverSearchSelect: React.FC<Omit<UserSearchSelectProps, 'fetchOptions'>> = (props) => {
    const fetchDriverList = async (search: string): Promise<UserValue[]> => {
        try {
            const response = await getPaginatedProviders({
                page: 1,
                take: 20,
                filters: JSON.stringify({
                    first_name: search,
                    last_name: search,
                    email: search,
                    mobile: search
                })
            });
            return response.data.map((provider: any) => ({
                label: `${provider.firstName} ${provider.lastName} (${provider.email || provider.mobile})`,
                value: provider.id.toString(),
            }));
        } catch (error) {
            console.error('Error fetching drivers:', error);
            return [];
        }
    };

    return (
        <DebounceSelect
            showSearch
            placeholder="Escriba nombre, email o teléfono"
            fetchOptions={fetchDriverList}
            {...props}
        />
    );
};

